import { useState } from 'react';
import {
  EmptyState,
  EmptyStateTitle,
  Flex,
  InlineStack,
  Link,
  Paragraph,
  SegmentedControl,
  Spinner,
  Stack,
  Strong,
  Table,
  Text,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
} from '@customerio/pluma-components/react';
import { EmailSearch } from '@/components/email-search';
import { createDefaultAttributeFilterState } from '@/features/condition-builder';
import { customerIdentifierDisplay } from '@/services/db/customer/display';
import type { Customer } from '@/services/db/customer/entity';
import { useRecipientSearch, type RecipientQuery } from '../hooks';
import { encodeRecipientFilters } from '../utils/recipient-filters';
import { RecipientFilterCondition } from './recipient-filter-condition';

/**
 * Who the event is sent for.
 *
 * A caller that already knows the profile — resending one that was recorded,
 * or sending from a profile's own page — passes it in, and the picker says
 * so with a way back to searching. Everyone else searches, by email address
 * or by one attribute condition — the only way in to a profile that has no
 * address at all.
 */

export type SendEventRecipient = {
  /** The internal id the send is addressed to. */
  cioId: string;
  /** How the profile is named in the modal. */
  label: string;
};

/** The recipient a search result describes. */
export function toRecipient(customer: Customer, personSingular: string): SendEventRecipient {
  return {
    // The endpoint addresses a send by internal id, which the profile carries
    // twice; `id` is the workspace's own identifier and is not one of them.
    cioId: customer.identifiers?.cio_id ?? customer.internal_id,
    label: customerIdentifierDisplay(customer, personSingular),
  };
}

/** What the workspace calls the people it holds. */
export type PeopleTerms = {
  /** "person", as it reads mid-sentence. */
  singular: string;
  /** "Person", as it reads where a product noun is named. */
  titleSingular: string;
  /** "People". */
  titlePlural: string;
};

type RecipientPickerProps = {
  workspaceId: number;
  recipient: SendEventRecipient | null;
  onChange: (recipient: SendEventRecipient | null) => void;
  terms: PeopleTerms;
  /** The viewer may not send at all, so there is nobody to pick. */
  isDisabled: boolean;
};

export function RecipientPicker({ workspaceId, recipient, onChange, terms, isDisabled }: RecipientPickerProps) {
  if (recipient != null) {
    return (
      <Paragraph>
        Send an event for <Strong className="fs-mask">{recipient.label}</Strong>.{' '}
        <Link isUnderlined isDisabled={isDisabled} onClick={() => onChange(null)}>
          Search for another {terms.titleSingular}.
        </Link>
      </Paragraph>
    );
  }

  // Unmounted while a profile is chosen, so going back to searching starts
  // from a blank email search, as Ember's does.
  return (
    <RecipientSearch
      workspaceId={workspaceId}
      terms={terms}
      isDisabled={isDisabled}
      onSelect={(customer) => onChange(toRecipient(customer, terms.titleSingular))}
    />
  );
}

/** How the profile is looked for: Ember's "Search by" choice. */
type SearchMode = 'email' | 'filters';

const SEARCH_MODE_OPTIONS = [
  { value: 'email', label: 'email address' },
  { value: 'filters', label: 'filters' },
];

type RecipientSearchProps = {
  workspaceId: number;
  terms: PeopleTerms;
  isDisabled: boolean;
  onSelect: (customer: Customer) => void;
};

function RecipientSearch({ workspaceId, terms, isDisabled, onSelect }: RecipientSearchProps) {
  const [mode, setMode] = useState<SearchMode>('email');
  const [query, setQuery] = useState<RecipientQuery | null>(null);
  // Kept across mode switches, as Ember keeps it: coming back to filters
  // shows the condition last applied, though its results were cleared.
  const [condition, setCondition] = useState(createDefaultAttributeFilterState);
  const [isEditingCondition, setIsEditingCondition] = useState(true);
  const search = useRecipientSearch(workspaceId, query);

  // Switching modes clears the search, as Ember's `onClearSearch` does: an
  // email search and a condition search never run together.
  const changeMode = (value: string) => {
    setMode(value === 'filters' ? 'filters' : 'email');
    setQuery(null);
  };

  return (
    <Stack gap="200">
      <Stack gap="150">
        <InlineStack alignBlock="center" alignInline="space-between">
          <SegmentedControl
            name="customer-search-by"
            ariaLabel="Search by"
            variant="primary"
            size="sm"
            value={mode}
            options={SEARCH_MODE_OPTIONS}
            isDisabled={isDisabled}
            onChange={changeMode}
          />
        </InlineStack>
        {mode === 'email' ? (
          <EmailSearch
            term={query != null && 'email' in query ? query.email : ''}
            onSearch={(email) => setQuery({ email })}
            autofocus
            isDisabled={isDisabled}
            // The field's own width, as Ember's send-event modal leaves it
            // (`customer-search`, no `@inputStyle`), so the search tips sit
            // beside it on one line rather than wrapping at the far edge.
            width="auto"
          />
        ) : (
          <RecipientFilterCondition
            workspaceId={workspaceId}
            condition={condition}
            isEditing={isEditingCondition}
            isDisabled={isDisabled}
            onApply={(applied) => {
              setCondition(applied);
              setIsEditingCondition(false);
              setQuery({ filters: encodeRecipientFilters(applied) });
            }}
            onEdit={() => setIsEditingCondition(true)}
            onDelete={() => {
              setCondition(createDefaultAttributeFilterState());
              setIsEditingCondition(true);
              setQuery(null);
            }}
          />
        )}
      </Stack>
      <RecipientResults
        customers={search.customers}
        isSearching={search.isSearching}
        hasSearched={search.hasSearched}
        terms={terms}
        isDisabled={isDisabled}
        onSelect={onSelect}
      />
    </Stack>
  );
}

type RecipientResultsProps = {
  customers: readonly Customer[];
  isSearching: boolean;
  hasSearched: boolean;
  terms: PeopleTerms;
  isDisabled: boolean;
  onSelect: (customer: Customer) => void;
};

function RecipientResults({ customers, isSearching, hasSearched, terms, isDisabled, onSelect }: RecipientResultsProps) {
  if (customers.length > 0) {
    return (
      <Table caption={`List of ${terms.titlePlural} matching your search`} layout="fixed">
        <TableThead>
          <TableTr>
            <TableTh>Email</TableTh>
            <TableTh>cio_id</TableTh>
            <TableTh />
          </TableTr>
        </TableThead>
        <TableTbody>
          {customers.map((customer) => (
            <TableTr key={customer.id}>
              <TableTd>
                {/* An address is subscriber data: keep it out of session replay.
                    `Truncated` would repeat it as tooltip content in a portal
                    that cannot carry the mask, so this truncates without one. */}
                <Text truncate className="fs-mask">
                  {customer.attributes?.email ?? 'No email'}
                </Text>
              </TableTd>
              <TableTd>
                {/* An email-identified workspace answers with the address as
                    the id, so this column carries the same subscriber data as
                    the one beside it, and hides it from replay the same way. */}
                <Text truncate className="fs-mask">
                  {customer.id}
                </Text>
              </TableTd>
              <TableTd textAlign="right">
                <Flex alignContent="center">
                  <Link
                    icon="chevron-right"
                    iconPosition="trailing"
                    isDisabled={isDisabled}
                    onClick={() => onSelect(customer)}
                  >
                    Select
                  </Link>
                </Flex>
              </TableTd>
            </TableTr>
          ))}
        </TableTbody>
      </Table>
    );
  }

  if (isSearching) {
    return (
      <Flex justifyContent="center" w="full" p="300">
        <Spinner />
      </Flex>
    );
  }

  if (hasSearched) {
    return (
      <EmptyState>
        <EmptyStateTitle icon="person">{`No ${terms.titlePlural} found`}</EmptyStateTitle>
      </EmptyState>
    );
  }

  return null;
}
